import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Calendar, IndianRupee, FileText } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => { const num = parseFloat(amount) || 0; return `${num < 0 ? '-' : ''}₹${Math.abs(num).toLocaleString('en-IN')}`; };
const formatMonth = (monthStr) => { if (!monthStr) return ''; const [year, month] = monthStr.split('-'); return new Date(year, parseInt(month) - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }); };

function SummaryCard({ title, amount, icon: Icon, gradient }) {
  const displayValue = formatCurrency(amount);
  const isLargeValue = displayValue && displayValue.length > 12;
  
  return (
    <Card className={`p-4 sm:p-5 border-0 rounded-2xl shadow-lg ${gradient} overflow-hidden`}>
      <div className="flex items-center gap-2 sm:gap-3 mb-2">
        <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg flex-shrink-0">
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
        <span className="text-xs sm:text-sm text-white/80 truncate">{title}</span>
      </div>
      <p 
        className={`font-bold text-white truncate ${isLargeValue ? 'text-base sm:text-xl' : 'text-xl sm:text-2xl'}`}
        style={{ fontFamily: "'Manrope', sans-serif" }}
        title={displayValue}
      >
        {displayValue}
      </p>
    </Card>
  );
}

function MonthlyTable({ data, title, desc }) {
  if (!data || data.length === 0) return <Card className="p-12 text-center border-0 rounded-2xl shadow-lg bg-white"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">No data available</p></Card>;
  const totals = data.reduce((acc, r) => ({ income: acc.income + r.income, expenses: acc.expenses + r.expenses, salaries: acc.salaries + r.salaries, profit: acc.profit + r.profit }), { income: 0, expenses: 0, salaries: 0, profit: 0 });
  return (
    <div className="space-y-4">
      <div><h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>{title}</h3><p className="text-sm text-slate-500">{desc}</p></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard title="Income" amount={totals.income} icon={TrendingUp} gradient="bg-gradient-to-br from-emerald-500 to-emerald-600" />
        <SummaryCard title="Expenses" amount={totals.expenses} icon={TrendingDown} gradient="bg-gradient-to-br from-red-500 to-red-600" />
        <SummaryCard title="Salaries" amount={totals.salaries} icon={IndianRupee} gradient="bg-gradient-to-br from-amber-500 to-amber-600" />
        <SummaryCard title="Net Profit" amount={totals.profit} icon={totals.profit >= 0 ? TrendingUp : TrendingDown} gradient={totals.profit >= 0 ? "bg-gradient-to-br from-slate-800 to-slate-900" : "bg-gradient-to-br from-red-600 to-red-700"} />
      </div>
      <Card className="border-0 rounded-2xl shadow-lg bg-white overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className="px-4 py-3 text-left font-semibold text-slate-700">Month</th><th className="px-4 py-3 text-right font-semibold text-slate-700">Income</th><th className="px-4 py-3 text-right font-semibold text-slate-700">Expenses</th><th className="px-4 py-3 text-right font-semibold text-slate-700">Salaries</th><th className="px-4 py-3 text-right font-semibold text-slate-700">Profit</th></tr></thead>
          <tbody className="divide-y divide-slate-100">{data.map((row, i) => (
            <tr key={i} className="hover:bg-slate-50"><td className="px-4 py-3 font-medium text-slate-900">{formatMonth(row.month)}</td><td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(row.income)}</td><td className="px-4 py-3 text-right text-red-600">{formatCurrency(row.expenses)}</td><td className="px-4 py-3 text-right text-amber-600">{formatCurrency(row.salaries)}</td><td className={`px-4 py-3 text-right font-bold ${row.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(row.profit)}</td></tr>
          ))}</tbody>
        </table></div>
      </Card>
    </div>
  );
}

function PeriodSummary({ data, title, desc }) {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <div><h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>{title}</h3><p className="text-sm text-slate-500">{desc}</p></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard title="Income" amount={data.income} icon={TrendingUp} gradient="bg-gradient-to-br from-emerald-500 to-emerald-600" />
        <SummaryCard title="Expenses" amount={data.expenses} icon={TrendingDown} gradient="bg-gradient-to-br from-red-500 to-red-600" />
        <SummaryCard title="Salaries" amount={data.salaries} icon={IndianRupee} gradient="bg-gradient-to-br from-amber-500 to-amber-600" />
        <SummaryCard title="Net Profit" amount={data.profit} icon={data.profit >= 0 ? TrendingUp : TrendingDown} gradient={data.profit >= 0 ? "bg-gradient-to-br from-slate-800 to-slate-900" : "bg-gradient-to-br from-red-600 to-red-700"} />
      </div>
      <Card className="p-5 border-0 rounded-2xl shadow-lg bg-white"><p className="text-sm text-slate-500"><span className="font-medium">Period:</span> {data.start_date} to {data.end_date}</p></Card>
    </div>
  );
}

export default function ProfitReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    try { const token = localStorage.getItem('token'); const response = await axios.get(`${API}/profit-report`, { headers: { Authorization: `Bearer ${token}` } }); setReport(response.data); }
    catch (error) { toast.error('Failed to load report'); } finally { setLoading(false); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="flex items-center gap-3 text-slate-500"><div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" /><span>Loading...</span></div></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6">
        <div><p className="text-amber-600 text-sm font-bold tracking-[0.15em] uppercase mb-1">Analytics</p><h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>Profit Report</h1><p className="text-slate-500 mt-1">Financial overview and performance metrics</p></div>
        
        <Tabs defaultValue="monthly-all" className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto gap-2 bg-transparent p-0">
            <TabsTrigger value="monthly-all" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl py-3">Monthly (All)</TabsTrigger>
            <TabsTrigger value="monthly-paid" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl py-3">Monthly (Paid)</TabsTrigger>
            <TabsTrigger value="ytd-all" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl py-3">YTD (All)</TabsTrigger>
            <TabsTrigger value="ytd-paid" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl py-3">YTD (Paid)</TabsTrigger>
          </TabsList>
          <TabsContent value="monthly-all" className="mt-6"><MonthlyTable data={report?.report1_monthly_all} title="Monthly Report (All Invoices)" desc="Includes all invoices regardless of payment status" /></TabsContent>
          <TabsContent value="monthly-paid" className="mt-6"><MonthlyTable data={report?.report2_monthly_paid_only} title="Monthly Report (Paid Only)" desc="Only includes paid invoices" /></TabsContent>
          <TabsContent value="ytd-all" className="mt-6"><PeriodSummary data={report?.report3_feb2_to_date_all} title="Feb 2nd to Date (All)" desc="All invoices from Feb 2nd to today" /></TabsContent>
          <TabsContent value="ytd-paid" className="mt-6"><PeriodSummary data={report?.report4_feb1_to_date_paid_only} title="Feb 1st to Date (Paid)" desc="Paid invoices from Feb 1st to today" /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
